import { Stack, styled } from '@mui/material'
import {
    assembleNameWithEmailFallback,
    isoDateToTimeIfTodayOrDate,
    joinDefinedStrings,
    type ThreadCopy,
    tokens,
    UserBubbleList,
    createAccountPresentation,
} from 'ComponentLibrary'
import { ChatsCircleIcon, PushPinIcon } from '@phosphor-icons/react'
import { simplifyMessagePreviewContent, ThreadWithMetadata } from 'ComponentLibrary/src/common/threads'
import { isEmpty } from 'lodash'
import { PinIconContainer } from '../ThreadSelection/ThreadSelectionItem'
import { mentionPattern } from 'ComponentLibrary/src/molecules/TextEditor/TextEditor'
import { TextWithEllipsis, Typography } from 'ComponentLibrary/src/piazza/text'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ThreadListItemProps<T> = {
    thread: ThreadWithMetadata<T>
    onClick: (thread: ThreadWithMetadata<T>) => void
    copy?: Partial<ThreadListPreviewItemCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

export type ThreadListPreviewItemCopy = {
    noTitle: string
    noMessagesInThread: string
    notifiedUsersTooltip: string
    thread: ThreadCopy
}

export const DEFAULT_THREAD_LIST_PREVIEW_ITEM_COPY: ThreadListPreviewItemCopy = {
    noTitle: 'Kein Titel',
    noMessagesInThread: 'Noch keine Nachrichten in dieser Unterhaltung',
    notifiedUsersTooltip: 'Über neue Nachrichten wird aktuell niemand benachrichtigt. Hier klicken, um Benachrichtigte hinzuzufügen.',
    thread: {
        noMessages: 'Keine Nachrichten',
        deletedMessage: 'Nachricht wurde gelöscht',
        createdBy: (userName) => `Unterhaltung wurde von ${userName} angelegt`,
        createdAutomatically: 'Die Unterhaltung wurde automatisch angelegt',
    },
}

function getMessagePreview<T>(thread: ThreadWithMetadata<T>, threadUserName?: string): string {
    const textWithBeautifiedMentions = thread.messages.at(-1)?.content?.replace(mentionPattern, (_match: string, id: string) => {
        const mentionedThreadParticipant = thread.participant_users.find((user) => user.id === parseInt(id))
        return `@${mentionedThreadParticipant ? assembleNameWithEmailFallback(mentionedThreadParticipant) : '?'}`
    })
    const sanitizedPreview = textWithBeautifiedMentions ? simplifyMessagePreviewContent(textWithBeautifiedMentions) : undefined
    return joinDefinedStrings([threadUserName, sanitizedPreview], ': ')
}

export function ThreadListPreviewItem<T>({ thread, onClick, copy, userLinkCopy, locale = 'de-DE' }: ThreadListItemProps<T>) {
    const resolvedCopy = { ...DEFAULT_THREAD_LIST_PREVIEW_ITEM_COPY, ...copy }
    const topic = thread.topic || resolvedCopy.noTitle
    const latestMessage = thread.messages.at(-1)
    // No thread username for system message to avoid double einwert text
    const threadUserName = latestMessage?.message_type === 'system_message' ? undefined : latestMessage?.user_name
    const latestMessagePreview = latestMessage ? getMessagePreview(thread, threadUserName) : resolvedCopy.noMessagesInThread

    return (
        <StyledStack direction="row" onClick={() => onClick(thread)}>
            {thread.isPinned && (
                <PinIconContainer>
                    <PushPinIcon weight="bold" color={tokens.ColorIconBoldest} size={13} />
                </PinIconContainer>
            )}
            <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center" width="100%" maxWidth="calc(100% - 80px)">
                <ChatsCircleIcon size={tokens.SizingIconM} style={{ flexShrink: 0 }} color={tokens.ColorIconBoldest} />
                <Stack direction="column" width="100%" maxWidth="calc(100% - 65px)">
                    <TextWithEllipsis tooltipText={topic} typographyProps={{ variant: 'textS', color: tokens.ColorIconLinkDefault }}>
                        {topic}
                    </TextWithEllipsis>
                    <TextWithEllipsis
                        tooltipText={latestMessagePreview}
                        typographyProps={{
                            variant: 'textS',
                            color: tokens.ColorTextDefault,
                        }}
                    >
                        {latestMessagePreview}
                    </TextWithEllipsis>
                </Stack>
            </Stack>
            <Stack direction="column" alignItems="flex-end" gap={tokens.SizingSpacing075}>
                <Typography variant="textXS" color={tokens.ColorTextDefault}>
                    {isoDateToTimeIfTodayOrDate(thread.updated_on, locale)}
                </Typography>
                {!isEmpty(thread.participant_users) && (
                    <UserBubbleList
                        tooltip={resolvedCopy.notifiedUsersTooltip}
                        presentations={thread.participant_users.map((user) => createAccountPresentation(user))}
                        userLinkCopy={userLinkCopy}
                    />
                )}
            </Stack>
        </StyledStack>
    )
}

const StyledStack = styled(Stack)(() => ({
    padding: '8px 16px',
    cursor: 'pointer',
    justifyContent: 'space-between',
    borderRadius: '8px',
    position: 'relative',
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
}))
